// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Tour/CountryList.jsx

import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { API_URLS, API_ENDPOINTS } from '../../api/api';

const emptyCountry = {
  country_NameTH: '',
  country_NameEN: '',
  country_Flag: '',
  country_Continent: 0,
};

const CountryList = ({ onDataChange }) => {
  const [countries, setCountries] = useState([]);
  const [continents, setContinents] = useState([]);
  const [countryDialog, setCountryDialog] = useState(false);
  const [country, setCountry] = useState(emptyCountry);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useRef(null);

  const token = localStorage.getItem('token');
  const authHeader = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
        setLoading(true);
        try {
            const [countriesRes, continentsRes] = await Promise.all([
                // [CHANGED] Use API_ENDPOINTS
                fetch(API_ENDPOINTS.COUNTRY_VIEW, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(API_ENDPOINTS.CONTINENT_VIEW, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);

            if (!countriesRes.ok) throw new Error('Failed to fetch countries');
            if (!continentsRes.ok) throw new Error('Failed to fetch continents');

            const countriesData = await countriesRes.json();
            const continentsData = await continentsRes.json();

            setCountries(Array.isArray(countriesData) ? countriesData : []);
            setContinents(Array.isArray(continentsData) ? continentsData : []);

        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        } finally {
            setLoading(false);
        }
    };
    fetchData();
  }, [token]); // Added token as a dependency

  const uploadFlag = async (file) => {
    const formData = new FormData();
    formData.append('uploadFile[]', file);
    try {
      // [CHANGED] Use API_URLS for image upload
      const response = await fetch(API_URLS.IMAGE_UPLOAD, { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Flag upload failed');
      const result = await response.json();
      return result.originalFileURLs[0];
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Upload Error', detail: error.message, life: 3000 });
      return null;
    }
  };

  const saveCountry = async () => {
    // [CHANGED] Use API_ENDPOINTS for create and edit
    let endpoint = API_ENDPOINTS.COUNTRY_CREATE;
    let method = 'POST';
    let body = { ...country };

    if (country.country_ID) {
      endpoint = API_ENDPOINTS.COUNTRY_EDIT;
      method = 'PUT';
    }

    if (selectedFile) {
      const flagUrl = await uploadFlag(selectedFile);
      if (!flagUrl) return;
      body.country_Flag = flagUrl;
    }

    try {
      const response = await fetch(endpoint, { 
        method, 
        headers: authHeader, 
        body: JSON.stringify(body) 
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
        throw new Error(errorData.message || `Failed to save country. Status: ${response.status}`);
      }
      
      setCountryDialog(false);
      setSelectedFile(null);
      toast.current.show({ severity: 'success', summary: 'Success', detail: 'Country Saved', life: 3000 });
      
      // [CHANGED] Use API_ENDPOINTS for refetching
      const res = await fetch(API_ENDPOINTS.COUNTRY_VIEW, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setCountries(Array.isArray(data) ? data : []);
      
      onDataChange();
    } catch (error)      {
      toast.current.show({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
    }
  };

  const deleteCountry = async (countryToDelete) => {
    try {
      // [CHANGED] Use API_ENDPOINTS for delete
      const response = await fetch(API_ENDPOINTS.COUNTRY_DELETE, {
        method: 'DELETE',
        headers: authHeader,
        body: JSON.stringify({ country_ID: [countryToDelete.country_ID] })
      });
      if (!response.ok) throw new Error('Failed to delete country');
      
      toast.current.show({ severity: 'success', summary: 'Success', detail: 'Country Deleted', life: 3000 });
      
      setCountries(prevCountries => prevCountries.filter(c => c.country_ID !== countryToDelete.country_ID));
      
      onDataChange();
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
    }
  };

  const confirmDelete = (countryToDelete) => {
    confirmDialog({
      message: `Are you sure you want to delete ${countryToDelete.country_NameEN}?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => deleteCountry(countryToDelete),
    });
  };

  const openNew = () => {
    setCountry(emptyCountry);
    setSelectedFile(null);
    setCountryDialog(true);
  };

  const editCountry = (countryToEdit) => {
    setCountry({ ...countryToEdit });
    setSelectedFile(null);
    setCountryDialog(true);
  };

  const hideDialog = () => setCountryDialog(false);

  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || '';
    setCountry(prev => ({ ...prev, [name]: val }));
  };

  const onContinentChange = (e) => {
    setCountry(prev => ({ ...prev, country_Continent: e.value }));
  };

  const onFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const actionBodyTemplate = (rowData) => (
    <React.Fragment>
      <Button type="button" icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editCountry(rowData)} />
      <Button type="button" icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDelete(rowData)} />
    </React.Fragment>
  );

  const continentBodyTemplate = (rowData) => {
    const continent = continents.find(c => c.Continent_ID === rowData.country_Continent);
    return continent ? continent.Continent_Name : 'N/A';
  };

  const countryDialogFooter = (
    <React.Fragment>
      <Button type="button" label="Cancel" icon="pi pi-times" outlined onClick={hideDialog} />
      <Button type="button" label="Save" icon="pi pi-check" onClick={saveCountry} />
    </React.Fragment>
  );
  
  const toolbarStart = <Button type="button" label="New Country" icon="pi pi-plus" severity="success" onClick={openNew} />;

  return (
    <div>
      <Toast ref={toast} />
      <Toolbar className="mb-4" start={toolbarStart} />
      <DataTable value={countries} loading={loading} dataKey="country_ID" tableStyle={{ minWidth: '50rem' }} paginator rows={10}>
        <Column field="country_ID" header="ID" sortable />
        <Column field="country_NameEN" header="Name (EN)" sortable filter filterPlaceholder="Search by name" />
        <Column field="country_NameTH" header="Name (TH)" sortable />
        <Column header="Continent" body={continentBodyTemplate} sortable sortField="country_Continent" />
        <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '12rem' }} />
      </DataTable>

      <Dialog visible={countryDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Country Details" modal className="p-fluid" footer={countryDialogFooter} onHide={hideDialog}>
        
        <div className="field">
          <label htmlFor="continent" className="font-bold">Continent</label>
          <Dropdown
            id="continent"
            value={country.country_Continent}
            options={continents}
            onChange={onContinentChange}
            optionLabel="Continent_Name"
            optionValue="Continent_ID"
            placeholder="Select a Continent"
          />
        </div>
        
        <div className="field">
          <label htmlFor="nameEN" className="font-bold">Name (EN)</label>
          <InputText id="nameEN" value={country.country_NameEN} onChange={(e) => onInputChange(e, 'country_NameEN')} required />
        </div>
        <div className="field">
          <label htmlFor="nameTH" className="font-bold">Name (TH)</label>
          <InputText id="nameTH" value={country.country_NameTH} onChange={(e) => onInputChange(e, 'country_NameTH')} required />
        </div>
        <div className="field">
          <label htmlFor="flag" className="font-bold">Flag</label>
          <input type="file" accept="image/*" onChange={onFileChange} className="p-inputtext" />
          {country.country_Flag && !selectedFile && <img src={country.country_Flag} alt="Flag" width="100" className="mt-2" />}
          {selectedFile && <img src={URL.createObjectURL(selectedFile)} alt="New Flag Preview" width="100" className="mt-2" />}
        </div>
      </Dialog>
    </div>
  );
};

export default CountryList;